import { isTruthy, setStateProperty } from '@ardoq/common-helpers';
import {
  ModalContainer,
  ModalHeader,
  ModalSize,
  ModalWrapper,
  StackModalContainer,
} from '@ardoq/modal';
import {
  action$,
  collectRoutines,
  connect,
  dispatchAction,
  extractPayload,
  ofType,
  reducedStream,
  reducer,
  spreadMap,
} from '@ardoq/rxbeach';
import { useEffect } from 'react';
import {
  combineLatest,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { APIAttachment, DocumentArchiveContext } from '@ardoq/api-types';
import {
  fetchWorkspaceDocumentArchiveAttachments,
  fetchWorkspaceDocumentArchiveFolders,
} from '../../streams/documentArchive/actions';
import { documentArchiveAttachments$ } from '../../streams/documentArchive/documentArchiveAttachments$';
import { documentArchiveFolders$ } from '../../streams/documentArchive/documentArchiveFolders$';
import { i18nLocale$ } from '../../streams/i18nLocale$';
import { locale$ } from '../../streams/locale$';
import { currentOrganizationId$ } from '../../streams/organizations/currentOrganizationId$';
import {
  workspaceDocumentArchiveCommands,
  workspaceDocumentArchiveRoutines,
} from '../../workspaceManager/pages/documentArchive/WorkspaceDocumentArchivePage';
import { dragState$ } from '../../workspaceManager/streams/dragState$';
import {
  documentArchiveRoutines,
  documentArchiveState$,
  globalDocumentArchiveAttachments$,
  globalDocumentArchiveCommands,
  globalDocumentArchiveFolders$,
} from '../DocumentArchivePage';
import {
  commitSelection,
  loadWorkspaceAttachments,
  setDocumentArchiveFilePickerActiveTab,
} from './actions';
import {
  DocumentArchiveFilePickerPageView,
  DocumentArchiveFilePickerPageViewProps,
} from './DocumentArchiveFilePickerPageView';
import { toDocumentArchiveFilePickerPageViewProps } from './toDocumentArchiveFilePickerPageViewProps';
import {
  FilePickerState,
  ImpureFilePickerState,
  CommittedAttachments,
} from './types';
import styled from 'styled-components';
import { radius } from '@ardoq/design-tokens';

const filePickerState$: Observable<FilePickerState> =
  reducedStream<FilePickerState>(
    'filePickerState$',
    {
      activeTab: 'global',
      committedSelection: [],
    },
    [
      reducer(
        setDocumentArchiveFilePickerActiveTab,
        setStateProperty('activeTab')
      ),
      reducer(commitSelection, setStateProperty('committedSelection')),
    ]
  );

const impureState$ = action$.pipe(
  ofType(loadWorkspaceAttachments),
  extractPayload(),
  startWith(null)
);

const workspaceContext$ = impureState$.pipe(
  map(state => state?.workspaceId),
  filter(isTruthy),
  tap(workspaceId => {
    dispatchAction(fetchWorkspaceDocumentArchiveAttachments(workspaceId));
    dispatchAction(fetchWorkspaceDocumentArchiveFolders(workspaceId));
  }),
  // shareReplay is needed because this stream has a side effect (fetch actions) in its tap.
  // Without it, every new subscription (e.g., via withLatestFrom in attachments$)
  // would re-trigger fetches, causing unwanted re-fetches during file uploads.
  shareReplay({ bufferSize: 1, refCount: true })
);

const workspaceAttachments$ = workspaceContext$.pipe(
  switchMap(workspaceId =>
    documentArchiveAttachments$.pipe(
      map(s => s.models),
      map(attachments =>
        attachments.filter(a => a.context === DocumentArchiveContext.WS)
      ),
      map(attachments =>
        attachments.filter(
          attachment => attachment.rootWorkspace === workspaceId
        )
      )
    )
  ),
  startWith([])
);

const workspaceFolders$ = workspaceContext$.pipe(
  switchMap(workspaceId =>
    documentArchiveFolders$.pipe(
      map(s => s.models),
      map(folders =>
        folders.filter(folder => folder.context === DocumentArchiveContext.WS)
      ),
      map(folders =>
        folders.filter(folder => folder.resource.id === workspaceId)
      )
    )
  ),
  startWith([])
);

type DocumentArchiveFilePickerPageImpureProps = {
  onCancel: VoidFunction;
  onFinish: (attachments: APIAttachment[]) => void;
  impureState: ImpureFilePickerState;
};

const ImpureView = ({
  impureState,
  onFinish,
  onCancel,
  committedAttachments,
  ...props
}: DocumentArchiveFilePickerPageViewProps &
  DocumentArchiveFilePickerPageImpureProps &
  CommittedAttachments) => {
  useEffect(() => {
    dispatchAction(loadWorkspaceAttachments(impureState));
  }, [impureState]);

  useEffect(() => {
    if (committedAttachments !== null) {
      onFinish(committedAttachments);
    }
  }, [committedAttachments, onFinish]);

  return (
    <StackModalContainer onBackdropClick={onCancel}>
      <ModalContainerWithBorderRadius modalSize={ModalSize.L}>
        <ModalHeader onCloseButtonClick={onCancel}>{props.title}</ModalHeader>
        <ModalWrapper>
          <DocumentArchiveFilePickerPageView {...props} />
        </ModalWrapper>
      </ModalContainerWithBorderRadius>
    </StackModalContainer>
  );
};

const ModalContainerWithBorderRadius = styled(ModalContainer)`
  border-radius: ${radius.medium};
  overflow: hidden;
`;

export const DocumentArchiveFilePickerPage = connect(
  ImpureView,
  combineLatest([
    documentArchiveState$,
    globalDocumentArchiveFolders$,
    globalDocumentArchiveAttachments$,
    currentOrganizationId$,
    locale$,
    of(globalDocumentArchiveCommands),
    filePickerState$,
    workspaceAttachments$,
    workspaceFolders$,
    of(workspaceDocumentArchiveCommands),
    dragState$,
    impureState$,
    i18nLocale$,
  ]).pipe(spreadMap(toDocumentArchiveFilePickerPageViewProps)),
  collectRoutines(documentArchiveRoutines, workspaceDocumentArchiveRoutines)
);
